import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  AnalyticsDashboard,
  AnalyticsFilters,
  AnalyticsFilterOption,
  DashboardAnalytics,
  InventoryAnalytics,
  LowStockItem,
  MaintenanceAnalytics,
  OperationsAnalytics,
  ProcurementAnalytics,
  PurchaseAnalytics,
  RecentActivity,
  ReportsAnalytics,
  SupplierAnalytics,
  TopMovingPart,
} from "../types";

const BASE_URL = "/analytics";

const normalizeFilters = (filters: AnalyticsFilters = {}) => ({
  ...filters,
  fromDate: filters.fromDate || filters.startDate || undefined,
  toDate: filters.toDate || filters.endDate || undefined,
  warehouseId: filters.warehouseId || filters.hubId || undefined,
  technicianId: filters.technicianId || filters.employeeId || undefined,
});

const emptyDashboard: AnalyticsDashboard = {
  inventory: {
    totalParts: 0,
    totalValue: 0,
    lowStockItems: 0,
  },
  maintenance: {
    openTickets: 0,
  },
  supplier: {
    activeSuppliers: 0,
  },
  preventiveMaintenance: {
    totalSchedules: 0,
    dueToday: 0,
    dueSoon: 0,
    overdue: 0,
    completedToday: 0,
  },
  purchaseTrend: [],
  stockMovement: [],
};

class AnalyticsService {
  async getHubs(): Promise<AnalyticsFilterOption[]> {
    try {
      const response = await sparePartsApi.get<ApiResponse<any[]>>(
        "/integrations/hubs"
      );

      return (response.data.data || []).map((hub) => ({
        id: hub.id,
        name: hub.code ? `${hub.name} (${hub.code})` : hub.name,
        code: hub.code,
      }));
    } catch {
      return [];
    }
  }

  async getCategories(): Promise<AnalyticsFilterOption[]> {
    try {
      const response = await sparePartsApi.get<ApiResponse<any[]>>(
        "/categories",
        { params: { page: 1, limit: 100 } }
      );

      return (response.data.data || []).map((category) => ({
        id: category.id,
        name: category.name,
        code: category.code,
      }));
    } catch {
      return [];
    }
  }

  async getSuppliers(): Promise<AnalyticsFilterOption[]> {
    try {
      const response = await sparePartsApi.get<ApiResponse<any[]>>(
        "/suppliers",
        { params: { page: 1, limit: 100 } }
      );

      return (response.data.data || []).map((supplier) => ({
        id: supplier.id,
        name: supplier.supplierName || supplier.name,
        code: supplier.code,
      }));
    } catch {
      return [];
    }
  }

  async getDashboardAnalytics(
    filters?: AnalyticsFilters
  ): Promise<DashboardAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<DashboardAnalytics>>(
      `${BASE_URL}/dashboard`,
      { params: normalizeFilters(filters) }
    );

    return response.data.data;
  }

  async getInventoryAnalytics(
    filters?: AnalyticsFilters
  ): Promise<InventoryAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<InventoryAnalytics>>(
      `${BASE_URL}/inventory`,
      { params: normalizeFilters(filters) }
    );

    const data = response.data.data;
    return {
      ...data,
      totalParts: data.warehouseWise?.reduce(
        (sum, item) => sum + Number(item.totalParts || 0),
        0
      ),
      totalQuantity: data.totalStock,
      totalValue: data.inventoryValue,
      lowStockItems: data.lowStock,
      outOfStockItems: data.outOfStock,
      categories: data.categoryWise?.length || 0,
    };
  }

  async getProcurementAnalytics(
    filters?: AnalyticsFilters
  ): Promise<ProcurementAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<ProcurementAnalytics>>(
      `${BASE_URL}/procurement`,
      { params: normalizeFilters(filters) }
    );

    return response.data.data;
  }

  async getMaintenanceAnalytics(
    filters?: AnalyticsFilters
  ): Promise<MaintenanceAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<MaintenanceAnalytics>>(
      `${BASE_URL}/maintenance`,
      { params: normalizeFilters(filters) }
    );

    const data = response.data.data;
    return {
      ...data,
      totalTickets: data.tickets,
      assignedTickets: data.assignments,
      inProgressTickets: data.openTickets,
      overdueMaintenance: data.preventiveSchedule?.due || 0,
    };
  }

  async getOperationsAnalytics(
    filters?: AnalyticsFilters
  ): Promise<OperationsAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<OperationsAnalytics>>(
      `${BASE_URL}/operations`,
      { params: normalizeFilters(filters) }
    );

    return response.data.data;
  }

  async getReportsAnalytics(
    filters?: AnalyticsFilters
  ): Promise<ReportsAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<ReportsAnalytics>>(
      `${BASE_URL}/reports`,
      { params: normalizeFilters(filters) }
    );

    return response.data.data;
  }

  toDashboardView(
    dashboard: DashboardAnalytics | null,
    procurement: ProcurementAnalytics | null,
    maintenance: MaintenanceAnalytics | null,
    reports: ReportsAnalytics | null
  ): AnalyticsDashboard {
    if (!dashboard) return emptyDashboard;

    return {
      inventory: {
        totalParts: dashboard.totalSpareParts,
        totalValue: dashboard.inventoryValue,
        lowStockItems: dashboard.lowStock,
      },
      maintenance: {
        openTickets: maintenance?.openTickets || 0,
      },
      supplier: {
        activeSuppliers: dashboard.totalSuppliers,
      },
      preventiveMaintenance: {
        totalSchedules: maintenance?.preventiveSchedule?.total || 0,
        dueToday: maintenance?.preventiveSchedule?.due || 0,
        dueSoon: maintenance?.preventiveSchedule?.upcoming || 0,
        overdue: maintenance?.preventiveSchedule?.due || 0,
        completedToday: 0,
      },
      purchaseTrend:
        procurement?.monthlyPurchase?.map((item) => ({
          month: item.month,
          amount: Number(item.purchaseCost || 0),
        })) || [],
      stockMovement:
        reports?.monthly?.map((item) => ({
          month: item.month,
          inward: Number(item.inwardQuantity || 0),
          outward: Number(item.issueQuantity || 0),
        })) || [],
    };
  }

  toPurchaseView(procurement: ProcurementAnalytics | null): PurchaseAnalytics {
    return {
      totalBills: procurement?.purchaseBills || 0,
      pendingBills: procurement?.pendingBills || 0,
      completedBills: procurement?.completedBills || 0,
      cancelledBills: procurement?.cancelledBills || 0,
      totalPurchaseAmount: procurement?.purchaseCost || 0,
    };
  }

  toSupplierView(procurement: ProcurementAnalytics | null): SupplierAnalytics {
    const suppliers = procurement?.supplierPerformance || [];
    const topSupplier = [...suppliers].sort(
      (a, b) => Number(b.purchaseCost || 0) - Number(a.purchaseCost || 0)
    )[0];

    return {
      totalSuppliers: suppliers.length,
      activeSuppliers: suppliers.length,
      inactiveSuppliers: 0,
      topSupplier: topSupplier?.supplierName,
    };
  }

  toTopMovingParts(inventory: InventoryAnalytics | null): TopMovingPart[] {
    return (inventory?.fastMovingParts || []).map((item) => ({
      id: item.sparePartId,
      partCode: item.code,
      partName: item.name,
      issuedQuantity: Number(item.quantity || 0),
      availableQuantity: Number(item.availableQuantity || 0),
      reorderLevel: Number(item.reorderLevel || 0),
    }));
  }

  toLowStockItems(inventory: InventoryAnalytics | null): LowStockItem[] {
    return (inventory?.deadStock || []).map((item) => ({
      id: item.stockId,
      partCode: item.code,
      partName: item.name,
      availableQuantity: Number(item.availableQuantity || 0),
      minimumStock: Math.max(Number(item.availableQuantity || 0), 1),
    }));
  }

  toRecentActivities(reports: ReportsAnalytics | null): RecentActivity[] {
    return (reports?.monthly || []).slice(-10).map((item) => ({
      id: item.month,
      title: item.month,
      description: `Purchases ${item.purchaseCost}, issues ${item.issueQuantity}`,
      createdAt: item.month,
    }));
  }
}

export default new AnalyticsService();
